import {useColorTheme} from './color-theme.jsx';
import { useState, useEffect, useMemo, useId } from 'react';
import { HEAT_RAMP } from './shared.jsx';
import { brand } from '../brand.js';
import { MOTION, EntranceGroup, lineDataSig, useGrowCycle } from './motion.jsx';
import { requireStrings } from './required-strings.js';

// ── Required text (DEC-10) ────────────────────────────────
// Every word the maps draw comes from the caller, translated. No English
// default: a missing key throws (see required-strings.js).
//   heading   side-panel header ("By country" / "By region")
//   scale     legend title
//   low/high  ends of the legend ramp
//   noData    hatched legend entry + tooltip of a region without a value
//   loading   placeholder while the topology downloads
//   loadError prefix of the fail-loud card (audit #220)
const MAP_STRING_KEYS = ['heading', 'scale', 'low', 'high', 'noData', 'loading', 'loadError'];

// ── "No data" (DEC-10) ────────────────────────────────────
// A region without a value is NOT the lowest step of the ramp: missing is
// not zero. It gets a hatch — a texture, not only a colour, because the
// light ramp step and a flat neutral are hard to tell apart (and for a
// red-green colour-blind reader, impossible). The legend carries the
// same hatch with the `noData` label.
const HATCH_INK = brand.neutral.faint;
const HATCH_CSS = `repeating-linear-gradient(45deg, ${HATCH_INK} 0 1.5px, white 1.5px 5px)`;
function hasValue(info) {
  return info != null && typeof info.numValue === 'number' && Number.isFinite(info.numValue);
}
function HatchPattern({ id }) {
  return (
    <defs>
      <pattern id={id} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="5" height="5" fill="white" />
        <line x1="0" y1="0" x2="0" y2="5" stroke={HATCH_INK} strokeWidth="1.5" />
      </pattern>
    </defs>
  );
}
function NoDataLegend({ label }) {
  return (
    <div className="flex items-center gap-1.5 mt-2" data-pm-map-nodata="">
      <div className="w-3 h-3 rounded-sm border border-neutral-300" style={{ background: HATCH_CSS }} />
      <span className="text-2xs text-neutral-600">{label}</span>
    </div>
  );
}

// ── Heat helpers (redesign v2) ────────────────────────────
// Single ramp resolver for both maps: normalized value → step of the
// shared magenta HEAT_RAMP. Same thresholds the ChileMap used inline.
// Only ever called with a finite value — a missing one is hatched, never
// coerced to 0 (DEC-10).
function rampColor(numVal, maxVal, scale = HEAT_RAMP) {
  const t = numVal / (maxVal || 1);
  return scale[Math.min(scale.length - 1, Math.max(0, Math.floor(t * scale.length)))];
}
// Contrast for on-map value labels: white on the dark half of the
// ramp, neutral chart text on the light half (works for arbitrary
// `color` overrides too via relative luminance).
function labelColorFor(fill) {
  const h = String(fill).replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return brand.neutral.text;
  const lum = (0.299 * parseInt(h.slice(0, 2), 16) + 0.587 * parseInt(h.slice(2, 4), 16) + 0.114 * parseInt(h.slice(4, 6), 16)) / 255;
  return lum < 0.55 ? 'white' : brand.neutral.text;
}

// ── Projection helpers ────────────────────────────────────
function mercatorX(lon, w, lonMin, lonMax) {
  return ((lon - lonMin) / (lonMax - lonMin)) * w;
}
function mercatorY(lat, h, latMin, latMax) {
  const latRad = (lat * Math.PI) / 180;
  const latMinRad = (latMin * Math.PI) / 180;
  const latMaxRad = (latMax * Math.PI) / 180;
  const yNorm =
    (Math.log(Math.tan(Math.PI / 4 + latRad / 2)) - Math.log(Math.tan(Math.PI / 4 + latMinRad / 2))) /
    (Math.log(Math.tan(Math.PI / 4 + latMaxRad / 2)) - Math.log(Math.tan(Math.PI / 4 + latMinRad / 2)));
  return h * (1 - yNorm);
}
function geoToSvgPath(coords, w, h, lonMin, lonMax, latMin, latMax) {
  if (!coords || coords.length === 0) return '';
  return coords.map(ring => {
    if (!ring || ring.length === 0) return '';
    return ring.map((pt, i) => {
      const x = mercatorX(pt[0], w, lonMin, lonMax);
      const y = mercatorY(pt[1], h, latMin, latMax);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ') + 'Z';
  }).join(' ');
}
function ringArea(ring) {
  return ring.reduce((sum, p, i) => { const q = ring[(i+1)%ring.length]; return sum + p[0]*q[1]-q[0]*p[1]; }, 0)/2;
}
function centroidFromCoords(coords, w, h, lonMin, lonMax, latMin, latMax) {
  let sumX = 0, sumY = 0, count = 0;
  (coords || []).forEach(ring => {
    (ring || []).forEach(pt => {
      sumX += mercatorX(pt[0], w, lonMin, lonMax);
      sumY += mercatorY(pt[1], h, latMin, latMax);
      count++;
    });
  });
  return count > 0 ? { x: sumX / count, y: sumY / count } : { x: 0, y: 0 };
}

// ── TopoJSON minimal decoder ──────────────────────────────
function topoToGeo(topology, objectName) {
  const obj = topology.objects[objectName];
  if (!obj) return [];
  const { arcs, transform } = topology;

  function decodeArc(arcIdx) {
    const reverse = arcIdx < 0;
    const arc = arcs[reverse ? ~arcIdx : arcIdx];
    if (!arc) return [];
    let x = 0, y = 0;
    const coords = arc.map(pt => {
      x += pt[0]; y += pt[1];
      return transform
        ? [x * transform.scale[0] + transform.translate[0], y * transform.scale[1] + transform.translate[1]]
        : [x, y];
    });
    return reverse ? coords.slice().reverse() : coords;
  }

  function decodeRing(arcIndices) {
    return arcIndices.reduce((acc, idx) => {
      const decoded = decodeArc(idx);
      return acc.concat(acc.length > 0 ? decoded.slice(1) : decoded);
    }, []);
  }

  return (obj.geometries || []).map(geom => {
    let coordinates = [];
    if (geom.type === 'Polygon') coordinates = geom.arcs.map(ring => decodeRing(ring));
    else if (geom.type === 'MultiPolygon') coordinates = geom.arcs.map(p => p.map(ring => decodeRing(ring)));
    return { type: 'Feature', id: geom.id, properties: geom.properties || {}, geometry: { type: geom.type, coordinates } };
  });
}

// ── Map card vertical chrome (uniform-grid contract) ──────
//
// The maps render their OWN card (they skip ChartCard). Measured from
// the markup below — if the title bar / borders change, this constant
// must move with them (review #12: the constant lives next to the
// markup it measures):
//
//   title bar px-5 py-3   24px (12 top + 12 bottom)
//   title line            20px (text-sm ⇒ 20px line-height)
//   title border-b         1px
//   card border            2px (1 top + 1 bottom)
//   ──────────────────────────
//   total                 47px
//
// MIRROR consumer: packages/dashboard-render/src/GenericChart.tsx
// (`cellPx − MAP_CARD_CHROME_PX` = the map body height that makes the
// whole card land exactly on its grid cell).
export const MAP_CARD_CHROME_PX = 47;

// ── South America ─────────────────────────────────────────
const SA_COUNTRY_IDS = {
  '032': { id: 'AR', name: 'Argentina' }, '068': { id: 'BO', name: 'Bolivia' },
  '076': { id: 'BR', name: 'Brazil' },   '152': { id: 'CL', name: 'Chile' },
  '170': { id: 'CO', name: 'Colombia' }, '218': { id: 'EC', name: 'Ecuador' },
  '254': { id: 'GF', name: 'French Guiana' }, '328': { id: 'GY', name: 'Guyana' },
  '600': { id: 'PY', name: 'Paraguay' }, '604': { id: 'PE', name: 'Peru' },
  '740': { id: 'SR', name: 'Suriname' }, '858': { id: 'UY', name: 'Uruguay' },
  '862': { id: 'VE', name: 'Venezuela' },
};
const SA_IDS_SET = new Set(Object.keys(SA_COUNTRY_IDS));

export function SouthAmericaMap({ data, title, height = 460, entrance = true, strings, colorScale }) {
  const clientTheme = useColorTheme('SouthAmericaMap');
  if (clientTheme.override) colorScale = clientTheme.heatScale;
  const scaleColors = colorScale?.length ? colorScale : clientTheme.heatScale;
  const s = requireStrings('SouthAmericaMap', strings, MAP_STRING_KEYS);
  const hatchId = `pm-map-nodata-${useId().replace(/[^\w-]/g, '')}`;
  const [hover, setHover] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  // Fail loud (owner decision, audit #220): a topology that can't load is
  // an error card with its reason — never an empty map that reads "no data".
  const [loadError, setLoadError] = useState(null);
  const W = 500, H = 550, lonMin = -82, lonMax = -28, latMin = -56, latMax = 14;

  useEffect(() => {
    fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json')
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(topo => {
        const objKey = Object.keys(topo.objects)[0];
        const feats = topoToGeo(topo, objKey).filter(f => SA_IDS_SET.has(String(f.id)));
        if (feats.length === 0) throw new Error('topology has no South American countries');
        setFeatures(feats);
        setLoading(false);
      })
      .catch(e => { setLoadError(e instanceof Error ? e.message : String(e)); setLoading(false); });
  }, []);

  const paths = useMemo(() => features.map(f => {
    const meta = SA_COUNTRY_IDS[String(f.id)];
    if (!meta) return null;
    const { type, coordinates } = f.geometry;
    let allPaths = '', allCoords = [];
    const addRing = ring => { allPaths += geoToSvgPath([ring], W, H, lonMin, lonMax, latMin, latMax) + ' '; allCoords.push(ring); };
    if (type === 'Polygon') coordinates.forEach(addRing);
    else if (type === 'MultiPolygon') coordinates.forEach(p => p.forEach(addRing));
    return { ...meta, d: allPaths.trim(), ...centroidFromCoords([allCoords.slice().sort((a,b) => Math.abs(ringArea(b)) - Math.abs(ringArea(a)))[0]], W, H, lonMin, lonMax, latMin, latMax) };
  }).filter(Boolean), [features]);

  // Chart-level cycle (r16, migrated 2026-07-24): the coloured regions
  // fade in on first load AND re-fade together whenever the map data
  // changes, like every other widget when a filter lands. The geometry
  // count is PART of the signature on purpose: this map paints nothing
  // until the CDN topology resolves, which routinely lands after the
  // shared entrance window — keying on the values alone would leave a
  // slow-loading map mounting dead (the v1 table-cascade failure mode,
  // PR #182). First geometry landing ⇒ key change ⇒ the fade plays.
  const regionsDrawn = useGrowCycle(
    useMemo(() => lineDataSig([paths.length, data]), [paths.length, data]),
    !entrance,
  );

  // Intensity scale: shared magenta HEAT_RAMP (redesign F3b). Fill is
  // computed from `numValue` (same contract GenericChart emits); an
  // explicit `color` per entry still wins (legacy override).

  const maxVal = Math.max(...Object.values(data || {}).filter(hasValue).map(d => d.numValue), 1);
  // No value → hatch (DEC-10); it used to be HEAT_RAMP[0], the legend's "Low".
  const fillFor = (info) => info?.color || (hasValue(info) ? rampColor(info.numValue, maxVal, scaleColors) : `url(#${hatchId})`);
  const swatchFor = (info) => info?.color || (hasValue(info) ? rampColor(info.numValue, maxVal, scaleColors) : HATCH_CSS);

  return (
    <div className="rounded-[14px] border border-neutral-200 bg-white shadow-card overflow-hidden">
      {title && (
        <div className="px-5 py-3 border-b border-neutral-100">
          <h3 className="text-sm font-bold text-neutral-800">{title}</h3>
        </div>
      )}
      <div data-map-body="" className="flex max-sm:flex-col" style={{ minHeight: height }}>
        <div data-map-plot="" className="flex-1 min-w-0 p-3 bg-white flex items-center justify-center max-sm:w-full">
          {loading ? (
            <div className="text-xs text-neutral-500" data-pm-loading="">{s.loading}</div>
          ) : loadError ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-negative">
              {s.loadError} ({loadError})
            </div>
          ) : (
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: height - 16 }}>
              <HatchPattern id={hatchId} />
              {/* Entrance (ronda 12 sweep): fade — scaling a map lies
                  about geography. Data-coloured regions + their value
                  labels fade in together; the hover tooltip stays
                  outside the group (interaction chrome, not a mark). */}
              <EntranceGroup mode="fade" disabled={!entrance} drawn={regionsDrawn}>
              {paths.map(p => {
                const info = data?.[p.id];
                const baseColor = fillFor(info);
                const isH = hover === p.id;
                return (
                  <g key={p.id} data-country-id={p.id} role="button" tabIndex={0} aria-label={p.name+': '+(hasValue(info)?info.value:s.noData)}
                    onFocus={()=>setHover(p.id)} onBlur={()=>setHover(null)} onClick={()=>setHover(p.id)}
                    onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();setHover(p.id)}if(e.key==='Escape')setHover(null)}} onMouseEnter={() => setHover(p.id)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }}>
                    <path d={p.d} vectorEffect="non-scaling-stroke" fill={baseColor}
                      opacity={hover === null || isH ? 0.9 : 0.35}
                      stroke={isH ? brand.accent.DEFAULT : brand.neutral.axis} strokeWidth={isH ? 2 : 0.5} strokeLinejoin="round"
                      style={{ transition: `opacity ${MOTION.durFast}ms ${MOTION.easeOut}`, filter: isH ? 'drop-shadow(0 3px 6px rgba(0,0,0,0.2))' : 'none' }} />
                  </g>
                );
              })}
              {/* On-map value labels (redesign rule 1 — values visible
                  where they fit; skipped while that country is hovered
                  so the tooltip takes over). */}
              {paths.map(p => {
                const info = data?.[p.id];
                if (!info || hover === p.id || (!hasValue(info) && !info.color)) return null;
                return (
                  <text key={`lbl-${p.id}`} x={p.x} y={p.y} textAnchor="middle"
                    fontSize="11" fontWeight="700" fill={labelColorFor(fillFor(info))}
                    style={{ pointerEvents: 'none' }}>
                    {info.value}
                  </text>
                );
              })}
              </EntranceGroup>
            </svg>
          )}
        </div>
        <div data-map-list="" className="w-52 shrink-0 border-l border-neutral-100 p-4 space-y-1 overflow-y-auto bg-white max-sm:w-full max-sm:border-l-0 max-sm:border-t max-sm:border-neutral-200">
          <div data-map-tooltip="" aria-live="polite" className="mb-3 rounded-lg border border-neutral-200 p-3" style={{minHeight:68, pointerEvents:'none'}}>
            <div className="text-xs font-semibold">{hover ? (paths.find(p=>p.id===hover)?.name || CL_REGION_SHORT[hover] || hover) : s.heading}</div>
            <div className="text-sm font-bold mt-1">{hover ? (data?.[hover]?.value ?? s.noData) : '\u2014'}</div>
          </div>
          {paths.slice().sort((a, b) => {
            const num = (id) => data?.[id]?.numValue ?? parseFloat((data?.[id]?.value ?? '0').replace(/[^0-9.]/g, ''));
            return num(b.id) - num(a.id);
          }).map(p => {
            const info = data?.[p.id], isH = hover === p.id;
            return (
              <div key={p.id} role="button" tabIndex={0} data-map-item={p.id} onFocus={()=>setHover(p.id)} onBlur={()=>setHover(null)} onClick={()=>setHover(p.id)} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();setHover(p.id)}}}
                className={`flex min-h-8 items-center justify-between gap-2.5 py-1.5 px-2 rounded-lg transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${isH ? 'bg-accent/10 shadow-sm' : 'hover:bg-accent/5'}`}
                onMouseEnter={() => setHover(p.id)} onMouseLeave={() => setHover(null)}>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ background: swatchFor(info) }} />
                  <span className={`text-xs ${isH ? 'font-bold text-accent-dark' : 'font-medium text-neutral-600'}`}>{p.name}</span>
                </div>
                <span className="text-xs font-bold text-neutral-900">{hasValue(info)?info.value:s.noData}</span>
              </div>
            );
          })}
          <div className="mt-3 pt-3 border-t border-neutral-100">
            <p className="text-2xs font-semibold text-neutral-600 mb-1.5">{s.scale}</p>
            <div className="flex h-2 rounded-full overflow-hidden">
              {scaleColors.map((c, i) => <div key={i} className="flex-1 h-full" style={{ background: c }} />)}
            </div>
            <div className="flex justify-between mt-0.5">
              <span className="text-2xs text-neutral-600">{s.low}</span>
              <span className="text-2xs text-neutral-600">{s.high}</span>
            </div>
            <NoDataLegend label={s.noData} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Chile Map ─────────────────────────────────────────────
const CL_REGION_SHORT = {
  XV:'Arica y Parinacota', I:'Tarapacá', II:'Antofagasta', III:'Atacama',
  IV:'Coquimbo', V:'Valparaíso', RM:'Metropolitana', VI:"O'Higgins",
  VII:'Maule', XVI:'Ñuble', VIII:'Biobío', IX:'La Araucanía',
  XIV:'Los Ríos', X:'Los Lagos', XI:'Aysén', XII:'Magallanes',
};

const REGION_ORDER = ['XV','I','II','III','IV','V','RM','VI','VII','XVI','VIII','IX','XIV','X','XI','XII'];

export function ChileMap({ data, title, height = 520, entrance = true, strings, colorScale }) {
  const clientTheme = useColorTheme('ChileMap');
  if (clientTheme.override) colorScale = clientTheme.heatScale;
  const scaleColors = colorScale?.length ? colorScale : clientTheme.heatScale;
  const s = requireStrings('ChileMap', strings, MAP_STRING_KEYS);
  const hatchId = `pm-map-nodata-${useId().replace(/[^\w-]/g, '')}`;
  const [hover, setHover] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  // Fail loud (owner decision, audit #220): no approximate inline polygons
  // when the topology can't load — an error card with the reason instead.
  const [loadError, setLoadError] = useState(null);
  const W = 300, H = 700, lonMin = -76, lonMax = -66, latMin = -57, latMax = -17;
  const [view,setView] = useState('all');
  useEffect(() => {
    let active=true;
    import('../geo/chile-regions-dpa-2023.json').then(({default:geo})=>{
      const feats=geo.features.map(f=>({...f,regionId:f.properties.regionId,regionName:f.properties.regionName}));
      if(feats.length!==16||new Set(feats.map(f=>f.regionId)).size!==16)throw new Error('DPA asset must contain 16 unique regions');
      if(active){setFeatures(feats);setLoading(false);}
    }).catch(e=>{if(active){setLoadError(e instanceof Error?e.message:String(e));setLoading(false);}});
    return()=>{active=false};
  },[]);

  const maxVal = Math.max(...Object.values(data || {}).filter(hasValue).map(d => d.numValue), 1);
  // Steps from the shared magenta HEAT_RAMP (redesign F3b) via the
  // module-level `rampColor` — single resolver for both maps. A region
  // without a value is hatched, not painted "Low" (DEC-10).
  const intensityColor = (info) => (hasValue(info) ? rampColor(info.numValue, maxVal, scaleColors) : `url(#${hatchId})`);
  const swatchColor = (info) => info?.color || (hasValue(info) ? rampColor(info.numValue, maxVal, scaleColors) : HATCH_CSS);

  const paths = useMemo(() => features.map(f => {
    const { type, coordinates } = f.geometry;
    let allPaths = '', allCoords = [];
    const addRing = ring => { allPaths += geoToSvgPath([ring], W, H, lonMin, lonMax, latMin, latMax) + ' '; allCoords.push(ring); };
    if (type === 'Polygon') coordinates.forEach(addRing);
    else if (type === 'MultiPolygon') coordinates.forEach(p => p.forEach(addRing));
    const [lon,lat]=f.properties.labelPoint;
    return {id:f.regionId,name:f.regionName,d:allPaths.trim(),x:mercatorX(lon,W,lonMin,lonMax),y:mercatorY(lat,H,latMin,latMax)};
  }), [features]);



  // Chart-level cycle (r16, migrated 2026-07-24) — see SouthAmericaMap:
  // regions re-fade together on a data change, and the geometry count
  // in the signature covers the asynchronously imported DPA geometry landing after
  // the first render, so a late landing still animates.
  const regionsDrawn = useGrowCycle(
    useMemo(() => lineDataSig([paths.length, data]), [paths.length, data]),
    !entrance,
  );

  return (
    <div data-chile-map="" className="rounded-[14px] border border-neutral-200 bg-white shadow-card">
      {title && (
        <div className="px-5 py-3 border-b border-neutral-100 rounded-t-[14px]">
          <h3 className="text-sm font-bold text-neutral-800">{title}</h3>
        </div>
      )}

      <div data-map-body="" className="flex max-sm:flex-col" style={{ minHeight: height }}>
        <div data-map-plot="" className="flex-1 min-w-0 p-3 bg-white flex flex-col items-center justify-center max-sm:w-full">
          <div data-map-zoom="" className="mb-2 flex w-full shrink-0 flex-wrap items-center gap-1.5 border-b border-neutral-200 px-1 py-[7px]">{[['all','Chile'],['nuble','Ñuble / Biobío']].map(([key,label])=><button key={key} type="button" aria-pressed={view===key} onClick={()=>{setView(key);setHover(null)}} className={`rounded-[7px] border px-2.5 py-1.5 text-2xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${view===key?'border-primary/40 bg-primary-soft text-primary':'border-neutral-200 bg-white text-neutral-700 hover:bg-primary-soft'}`}>{label}</button>)}<a href="https://geoportal.cl/geoportal/catalog/36391/Divisi%C3%B3n%20Pol%C3%ADtica%20Administrativa%202023" target="_blank" rel="noreferrer" className="ml-auto text-2xs text-neutral-500 hover:text-primary">SUBDERE · DPA 2023 ↗</a></div>
          {loading ? (
            <div className="text-xs text-neutral-500" data-pm-loading="">{s.loading}</div>
          ) : loadError ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-negative">
              {s.loadError} ({loadError})
            </div>
          ) : (
            <div className="relative" style={{height:view==='all'?height-64:Math.min(height-64,400),width:view==='nuble'?'100%':undefined,maxWidth:view==='nuble'?560:undefined}}>
              <svg viewBox={view==='all'?`0 0 ${W} ${H}`:`${mercatorX(-74.2,W,lonMin,lonMax)} ${mercatorY(-35.2,H,latMin,latMax)} ${mercatorX(-70,W,lonMin,lonMax)-mercatorX(-74.2,W,lonMin,lonMax)} ${mercatorY(-39,H,latMin,latMax)-mercatorY(-35.2,H,latMin,latMax)}`} className="h-full block" style={{height:'100%',width:view==='nuble'?'100%':undefined,maxWidth:view==='all'?260:560,overflow:'hidden'}}>
                <HatchPattern id={hatchId} />
                {/* Entrance (ronda 12 sweep): fade over the data-coloured
                    regions — scaling a map lies about geography. The
                    hover marker stays outside the group. */}
                <EntranceGroup mode="fade" disabled={!entrance} drawn={regionsDrawn}>
                {paths.map((p, idx) => {
                  const isH = hover === p.id;
                  return (
                    <g key={p.id} data-region-id={p.id} role="button" tabIndex={0} aria-label={(CL_REGION_SHORT[p.id]||p.name)+': '+(hasValue(data?.[p.id])?data[p.id].value:s.noData)}
                      onFocus={()=>setHover(p.id)} onBlur={()=>setHover(null)} onClick={()=>setHover(p.id)} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();setHover(p.id)}if(e.key==='Escape')setHover(null)}} onMouseEnter={() => setHover(p.id)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }}>
                      <path d={p.d} vectorEffect="non-scaling-stroke" fill={data?.[p.id]?.color || intensityColor(data?.[p.id])}
                        opacity={hover === null || isH ? 0.9 : 0.3}
                        stroke={isH ? brand.accent.DEFAULT : brand.neutral.axis} strokeWidth={isH ? 1.8 : 0.5} strokeLinejoin="round"
                        style={{ transition: `opacity ${MOTION.durFast}ms ${MOTION.easeOut}`, filter: isH ? 'drop-shadow(0 3px 6px rgba(0,0,0,0.2))' : 'none' }} />
                    </g>
                  );
                })}
                </EntranceGroup>
                {view==='nuble'&&paths.filter(p=>['VII','XVI','VIII'].includes(p.id)).map(p=><text key={p.id} x={p.x} y={p.y} fontSize="3.5" fontWeight="700" textAnchor="middle" fill={brand.neutral.text} pointerEvents="none">{CL_REGION_SHORT[p.id]}</text>)}
                {hover && view==='all' && (() => {
                  const p = paths.find(x => x.id === hover);
                  if (!p) return null;
                  return (
                    <g data-map-focus="" style={{ pointerEvents: 'none' }}>
                      <circle cx={p.x} cy={p.y} r="4" fill={brand.accent.DEFAULT} stroke="white" strokeWidth="1.5" />
                    </g>
                  );
                })()}
              </svg>

            </div>
          )}
        </div>
        <div data-map-list="" className="w-56 shrink-0 border-l border-neutral-100 p-4 overflow-y-auto bg-white max-sm:w-full max-sm:border-l-0 max-sm:border-t max-sm:border-neutral-200">
          <div data-map-tooltip="" aria-live="polite" className="mb-3 rounded-lg border border-neutral-200 p-3" style={{minHeight:68, pointerEvents:'none'}}>
            <div className="text-xs font-semibold">{hover ? (paths.find(p=>p.id===hover)?.name || CL_REGION_SHORT[hover] || hover) : s.heading}</div>
            <div className="text-sm font-bold mt-1">{hover ? (data?.[hover]?.value ?? s.noData) : '\u2014'}</div>
          </div>
          <div className="space-y-0.5">
            {REGION_ORDER.map(rid => {
              const info = data?.[rid], isH = hover === rid;
              return (
                <div key={rid} role="button" tabIndex={0} data-map-item={rid} onFocus={()=>setHover(rid)} onBlur={()=>setHover(null)} onClick={()=>setHover(rid)} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();setHover(rid)}}}
                  className={`flex min-h-8 items-center justify-between gap-2.5 py-1 px-2 rounded-md transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${isH ? 'bg-accent/10 shadow-sm' : 'hover:bg-accent/5'}`}
                  onMouseEnter={() => setHover(rid)} onMouseLeave={() => setHover(null)}>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-sm" style={{ background: swatchColor(info) }} />
                    <span className={`text-2xs ${isH ? 'font-bold text-accent-dark' : 'font-medium text-neutral-600'}`}>
                      {CL_REGION_SHORT[rid] || rid}
                    </span>
                  </div>
                  <span className="text-2xs font-bold text-neutral-800">{hasValue(info) ? info.value : '—'}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-neutral-100">
            <p className="text-2xs font-semibold text-neutral-600 mb-1.5">{s.scale}</p>
            <div className="flex h-2 rounded-full overflow-hidden">
              {scaleColors.map((c, i) => <div key={i} className="flex-1 h-full" style={{ background: c }} />)}
            </div>
            <div className="flex justify-between mt-0.5">
              <span className="text-2xs text-neutral-600">{s.low}</span>
              <span className="text-2xs text-neutral-600">{s.high}</span>
            </div>
            <NoDataLegend label={s.noData} />
          </div>
        </div>
      </div>
    </div>
  );
}
